import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, 
  Users, 
  MapPin, 
  Filter,
  UserPlus,
  ArrowLeft
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ISSUE_AREAS = [
  { id: 'environmental_justice', en: 'Environmental Justice', es: 'Justicia Ambiental' },
  { id: 'voting_rights', en: 'Voting Rights', es: 'Derechos de Voto' },
  { id: 'housing', en: 'Housing', es: 'Vivienda' },
  { id: 'reproductive_freedom', en: 'Reproductive Freedom', es: 'Libertad Reproductiva' },
  { id: 'immigration', en: 'Immigration', es: 'Inmigración' },
  { id: 'workers_rights', en: 'Workers\' Rights', es: 'Derechos Laborales' },
  { id: 'climate_justice', en: 'Climate Justice', es: 'Justicia Climática' },
  { id: 'police_accountability', en: 'Police Accountability', es: 'Responsabilidad Policial' },
  { id: 'education_equity', en: 'Education Equity', es: 'Equidad Educativa' },
  { id: 'healthcare_access', en: 'Healthcare Access', es: 'Acceso a Salud' },
];

const DiscoverAdvocates = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    issue_areas: '',
    location: '',
    skills: ''
  });

  useEffect(() => {
    fetchAdvocates();
  }, []);

  const fetchAdvocates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('polibot_token');
      const params = new URLSearchParams();
      if (filters.issue_areas) params.append('issue_areas', filters.issue_areas);
      if (filters.location) params.append('location', filters.location);
      if (filters.skills) params.append('skills', filters.skills);
      
      const response = await axios.get(`${API}/community/discover?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(response.data.results || []);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(language === 'es' ? 'Completa la configuración primero' : 'Complete setup first');
        navigate('/community/onboarding');
      } else {
        toast.error(language === 'es' ? 'Error al buscar' : 'Search failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAdvocates();
  };

  const getIssueLabel = (issueId) => {
    const issue = ISSUE_AREAS.find(i => i.id === issueId);
    return issue ? (language === 'es' ? issue.es : issue.en) : issueId.replace('_', ' ');
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="discover-loading">
        <Skeleton className="h-10 w-64" />
        <div className="flex gap-4">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-40" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="discover-advocates">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/community')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            {language === 'es' ? 'Descubrir Defensores' : 'Discover Advocates'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'es' 
              ? 'Encuentra personas que luchan por las mismas causas'
              : 'Find people fighting for the same causes'}
          </p>
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
        <Select 
          value={filters.issue_areas || "all"} 
          onValueChange={(v) => setFilters(prev => ({ ...prev, issue_areas: v === "all" ? "" : v }))}
        >
          <SelectTrigger className="w-full sm:w-[200px]" data-testid="issue-filter">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder={language === 'es' ? 'Área de Interés' : 'Issue Area'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{language === 'es' ? 'Todas' : 'All'}</SelectItem>
            {ISSUE_AREAS.map(issue => (
              <SelectItem key={issue.id} value={issue.id}>
                {language === 'es' ? issue.es : issue.en}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="relative flex-1">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={language === 'es' ? 'Ubicación (ciudad, estado)' : 'Location (city, state)'}
            value={filters.location}
            onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
            className="pl-10"
            data-testid="location-filter"
          />
        </div>
        
        <Button type="submit" data-testid="search-btn">
          <Search className="h-4 w-4 mr-2" />
          {language === 'es' ? 'Buscar' : 'Search'}
        </Button>
      </form>

      {/* Results Count */}
      <p className="text-sm text-muted-foreground">
        {results.length} {language === 'es' ? 'defensores encontrados' : 'advocates found'}
      </p>

      {/* Results Grid */}
      {results.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {results.map((result, index) => (
            <Card 
              key={index} 
              className="hover:border-primary/30 transition-colors"
              data-testid={`advocate-card-${index}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {Math.round(result.relevance_score)}% {language === 'es' ? 'coincidencia' : 'match'}
                  </Badge>
                </div>
                
                <h3 className="font-semibold">
                  {result.profile.display_name || (language === 'es' ? 'Defensor Anónimo' : 'Anonymous Advocate')}
                </h3>
                
                {result.profile.location_display && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <MapPin className="h-3 w-3" />
                    {result.profile.location_display}
                  </p>
                )}
                
                {result.profile.bio && (
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                    {result.profile.bio}
                  </p>
                )}
                
                <div className="flex flex-wrap gap-1 mt-3">
                  {result.shared_issues.slice(0, 3).map((issue, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {getIssueLabel(issue)}
                    </Badge>
                  ))}
                  {result.shared_issues.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{result.shared_issues.length - 3}
                    </Badge>
                  )}
                </div>
                
                {result.profile.skills && result.profile.skills.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {language === 'es' ? 'Habilidades' : 'Skills'}: {result.profile.skills.slice(0, 3).join(', ')}
                  </p>
                )}
                
                <Button 
                  className="w-full mt-4" 
                  variant="outline"
                  onClick={() => navigate(`/community/profile/${result.profile.id}`)}
                  data-testid={`view-profile-${index}`}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Ver Perfil' : 'View Profile'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="font-medium text-lg mb-2">
              {language === 'es' ? 'No se encontraron defensores' : 'No advocates found'}
            </h3>
            <p className="text-muted-foreground">
              {language === 'es' 
                ? 'Intenta ampliar tus filtros o vuelve más tarde'
                : 'Try broadening your filters or check back later'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DiscoverAdvocates;
